/**
 * Every browser-stored UI preference, declared once (T-150 decision 31, step 1.7).
 *
 * Each entry in `UI_PREFS` is a key family as it is stored, with `{placeholders}` for the parts that
 * vary: `{teamId}` for the team, other names for a list, table, section or record id. Existing keys
 * are registered exactly as they were found and are never renamed (decision C4): a new name wipes
 * people's choices. A new key is `jflo.<area>.<name>`, then `_{teamId}` when it belongs to a team
 * (`newPrefKey`).
 *
 * - `scope`: `team` for a team's data (record ids, filters, counts, dismissals, drafts), even when
 *   an old key has no team id in it; `browser` for pure layout, shared by every team here.
 * - `clearOnSignOut`: what signing out removes (decision 31 a): tokens, drafts and team data such
 *   as filters, the label queue and inbox drafts. Layout (columns, page sizes, widths, open
 *   sections, scroll) stays.
 * - `version`: the stored value's format. Every key starts at 1 (or the version its old name
 *   already carries, like `jflo:inbox-sort-v2`). When old values can't be read any more, give the
 *   pref a new key with `.v<N>` on its name and register both.
 *
 * Read and write through `usePref` (hooks/use-pref.ts) or `readPref`/`writePref`: every read and
 * write is guarded, and a write that storage refuses still holds for this page until it reloads.
 * This is the only module that touches localStorage and sessionStorage (an ESLint rule, T-220).
 */

import { STORAGE_KEYS } from '@shared/constants';
import { LIST_PAGE_SIZE_OPTIONS } from '@/lib/list-page-size';

export type PrefScope = 'team' | 'browser';
export type PrefStorage = 'local' | 'session';

/** The names inside `{…}` in a key template. */
export type KeyParams<K extends string> = K extends `${string}{${infer P}}${infer Rest}`
  ? P | KeyParams<Rest>
  : never;

export type PrefParams<K extends string> = { [P in KeyParams<K>]: string | null | undefined };

/** No argument for a key without placeholders, the placeholders' values otherwise. */
export type PrefKeyArgs<K extends string> = [KeyParams<K>] extends [never] ? [] : [params: PrefParams<K>];

export interface PrefDef<K extends string = string> {
  /** The key as stored, with `{placeholders}`. Never renamed (decision C4). */
  key: K;
  scope: PrefScope;
  storage: PrefStorage;
  version: number;
  clearOnSignOut: boolean;
  /** What it holds, and where (for anyone reading the registry). */
  holds: string;
  /**
   * For a kept pref that also holds some team data: the stored text without it (null removes the
   * key). Run at sign-out instead of removing it.
   */
  scrubOnSignOut?: (raw: string) => string | null;
  /** Work someone wrote and hasn't saved: Log out asks before sign-out removes it. */
  draft?: boolean;
}

/** A pref with its value type, for `usePref` and `readPref`/`writePref`. */
export interface Pref<T, K extends string = string> extends PrefDef<K> {
  defaultValue: T;
  /** The stored text to a value, or undefined when it can't be read (the default is used). */
  parse: (raw: string) => T | undefined;
  /** The value to store, or null to remove the key. */
  serialize: (value: T) => string | null;
}

type DefInput<K extends string> = Pick<PrefDef<K>, 'key' | 'scope' | 'clearOnSignOut' | 'holds'> &
  Partial<Pick<PrefDef<K>, 'storage' | 'version' | 'scrubOnSignOut' | 'draft'>>;

/**
 * The label options blob (zustand persist) can still hold the queue every team shared: `queue` and
 * `step` before v5, `legacyQueue` until it moves to its own key. Sign-out keeps the options only.
 */
function withoutLabelQueue(raw: string): string | null {
  const blob = JSON.parse(raw) as { state?: Record<string, unknown> } | null;
  const state = blob?.state;
  if (!state || typeof state !== 'object') return raw;
  if (!('queue' in state) && !('step' in state) && !('legacyQueue' in state)) return raw;
  const { queue: _queue, step: _step, legacyQueue: _legacyQueue, ...options } = state;
  return JSON.stringify({ ...blob, state: options });
}

function def<const K extends string>(input: DefInput<K>): PrefDef<K> {
  return { storage: 'local', version: 1, ...input };
}

function typed<const K extends string, T>(
  input: DefInput<K> & Pick<Pref<T, K>, 'defaultValue' | 'parse' | 'serialize'>
): Pref<T, K> {
  return { storage: 'local', version: 1, ...input };
}

/** Codecs for the common shapes. */
export const prefCodec = {
  /** Text as stored; null (nothing stored) is the default. */
  text: {
    defaultValue: null as string | null,
    parse: (raw: string) => raw,
    serialize: (value: string | null) => value,
  },
  /** A flag stored as two words; anything else is the default. */
  flag(defaultValue: boolean, on = 'on', off = 'off') {
    return {
      defaultValue,
      parse: (raw: string) => (raw === on ? true : raw === off ? false : undefined),
      serialize: (value: boolean) => (value ? on : off),
    };
  },
};

/**
 * The registry. Grouped by area; ids are `<area><Name>`. Report column prefs and list page sizes
 * keep their helpers (report-column-prefs.ts, list-page-size.ts) until step 2.7.
 */
export const UI_PREFS = {
  // ── Auth and session (the auth flow owns these; sign-out clears the team ones) ────────────────
  authTabSession: def({ key: 'auth-storage', scope: 'team', storage: 'session', clearOnSignOut: true, holds: "This tab's user and session (zustand persist, stores/authStore.ts). Also the legacy localStorage copy, removed at load (lib/tab-team.ts)." }),
  authLegacySharedSession: def({ key: 'auth-storage', scope: 'team', clearOnSignOut: true, holds: 'Legacy shared session from before T-155; removed at load (lib/tab-team.ts).' }),
  authLegacyToken: def({ key: STORAGE_KEYS.AUTH_TOKEN, scope: 'team', clearOnSignOut: true, holds: 'Legacy token (STORAGE_KEYS.AUTH_TOKEN); only ever removed.' }),
  authLegacyUser: def({ key: STORAGE_KEYS.USER, scope: 'team', clearOnSignOut: true, holds: 'Legacy user (STORAGE_KEYS.USER); only ever removed.' }),
  authLegacyInviteToken: def({ key: 'auth_token', scope: 'team', clearOnSignOut: true, holds: 'Legacy JWT copy from accepting an invite; removed at load (lib/tab-team.ts).' }),
  authLastTeam: def({ key: 'jflo-last-team-id', scope: 'team', clearOnSignOut: true, holds: 'The team a new tab opens on (STORAGE_KEYS.LAST_TEAM, lib/tab-team.ts).' }),
  authInProgress: def({ key: 'auth_in_progress', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Sign-in under way (SESSION_AUTH_KEYS); the sign-in flow clears it.' }),
  authOAuthDeparted: def({ key: 'oauth_departed_origin', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Left for Google mid sign-in (SESSION_AUTH_KEYS).' }),
  authRedirect: def({ key: 'auth_redirect', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'Where to go after signing in (SESSION_AUTH_KEYS); kept so /login can return there.' }),
  teamSettingsFallback: def({ key: 'team_settings_{teamId}', scope: 'team', clearOnSignOut: true, holds: "A team's settings; read by lib/formatters.ts, written by nothing now." }),
  appPreloadReloadAt: def({ key: 'jflo.preloadReloadAt', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: 'When the tab last reloaded after a deploy (index.tsx).' }),

  // ── OAuth callbacks: one-shot guards in this tab ──────────────────────────────────────────────
  oauthXeroSuccess: def({ key: 'xero_success_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: "A Xero callback's answer, by code prefix (lib/oauth-api.ts)." }),
  oauthXeroRequest: def({ key: 'xero_request_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Stops a second Xero code exchange (lib/oauth-api.ts).' }),
  oauthXeroTenant: def({ key: 'xero_tenant_{tenantId}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Stops a second Xero tenant choice (lib/oauth-api.ts).' }),
  oauthXeroProcessed: def({ key: 'xero_processed_code_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Xero code already handled (pages/xero-callback.tsx).' }),
  oauthMicrosoftCode: def({ key: 'microsoft_oauth_code_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Microsoft code already seen (pages/microsoft-callback.tsx).' }),
  oauthMetaCode: def({ key: 'meta_oauth_code_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Meta code already seen (pages/meta-callback.tsx).' }),
  oauthGoogleCode: def({ key: 'google_oauth_code_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Google code already seen (pages/google-callback.tsx).' }),
  oauthShopifyOk: def({ key: 'shopify_oauth_ok_{code}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Shopify callback done (pages/shopify-callback.tsx).' }),
  oauthShopifyReturn: def({ key: 'shopify_oauth_return', scope: 'browser', storage: 'session', clearOnSignOut: true, holds: 'Where to go after linking Shopify (pages/link-shopify.tsx, integrations).' }),

  // ── Labels ────────────────────────────────────────────────────────────────────────────────────
  labelsQueue: def({ key: 'jflo.labels.queue_{teamId}', scope: 'team', clearOnSignOut: true, holds: "A team's label queue (stores/label-queue-persist.ts)." }),
  labelsLegacyQueue: def({ key: 'jflo.labels.legacyQueue', scope: 'team', clearOnSignOut: true, holds: 'The old shared queue, waiting to be split by team (stores/label-queue-persist.ts).' }),
  labelsOptions: def({ key: 'jflo-label-options', scope: 'browser', version: 5, clearOnSignOut: false, scrubOnSignOut: withoutLabelQueue, holds: 'Label size, print format and options (zustand persist version 5, stores/labelStore.ts); sign-out takes out any old shared queue.' }),
  labelsZebraPrinter: def({ key: 'jflo-zebra-printer-id', scope: 'browser', clearOnSignOut: false, holds: 'Last Zebra printer (pages/labels/use-zebra-printer.ts).' }),

  // ── Lists and bulk edit: views, filters and drafts ────────────────────────────────────────────
  listPageSize: def({ key: 'list_page_size_{list}_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Rows per page for a list (useTablePaging listId, lib/list-page-size.ts). The sizes kept in the retired /products, /media and bulk-edit view blobs move here at load (lib/retired-list-views.ts).' }),
  // Retired by T-196 (lib/retired-list-views.ts moves them at load). Still registered so sign-out
  // clears a blob a tab on older code wrote after the move.
  productsListView: def({ key: 'products_list_view_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Retired: /products filters and view before T-196 (lib/retired-list-views.ts).' }),
  mediaListView: def({ key: 'media_list_view_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Retired: /media filters and view before T-196 (lib/retired-list-views.ts).' }),
  productsBulkEditView: def({ key: 'products_bulk_edit_view_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Retired: products bulk edit filters and view before T-196 (lib/retired-list-views.ts).' }),
  collectionsBulkEditView: def({ key: 'collections_bulk_edit_view_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Retired: collections bulk edit filters and view before T-196 (lib/retired-list-views.ts).' }),
  productsBulkEditDrafts: def({ key: 'products_bulk_edit_drafts_{teamId}', scope: 'team', storage: 'session', clearOnSignOut: true, draft: true, holds: 'Unsaved products bulk edits (pages/products-bulk-edit/draft-persistence.ts).' }),
  collectionsBulkEditDrafts: def({ key: 'collections_bulk_edit_drafts_{teamId}', scope: 'team', storage: 'session', clearOnSignOut: true, draft: true, holds: 'Unsaved collections bulk edits (pages/collections-bulk-edit/draft-persistence.ts).' }),
  collectionTreeStatuses: def({ key: 'collection_tree_product_statuses_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Product statuses counted in the collection tree (pages/collections/collectionProductCountFilter.ts).' }),

  // ── Column choices (layout, so kept at sign-out) ──────────────────────────────────────────────
  columnsMarkdowns: def({ key: 'markdowns_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsOverstock: def({ key: 'overstock_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsOutOfStock: def({ key: 'out_of_stock_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsPriceChanges: def({ key: 'price_changes_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsOrderReconciliation: def({ key: 'jflo.reports.orderReconciliation.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsOrphanVariantRevenue: def({ key: 'jflo.reports.orphanVariantRevenue.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsSupplierChanges: def({ key: 'jflo.reports.supplierChanges.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsAiUsage: def({ key: 'jflo.ai.usage.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsShopifyPayments: def({ key: 'jflo.reports.shopifyPayments.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsCollectionProblems: def({ key: 'jflo.reports.collectionProblems.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsGoogleAdsCheck: def({ key: 'jflo.reports.googleAdsCheck.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsWastedAdSpend: def({ key: 'jflo.reports.wastedAdSpend.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsMetaAdsCheck: def({ key: 'jflo.reports.metaAdsCheck.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsSwatchProblems: def({ key: 'jflo.reports.swatchProblems.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsXeroStockNotOnShopify: def({ key: 'jflo.reports.xeroStockNotOnShopify.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsShopifySales: def({ key: 'shopify_sales_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (reports/report-column-prefs.ts).' }),
  columnsSkuStock: def({ key: 'sku_stock_performance_hidden_columns_{teamId}_{groupBy}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns per grouping (reports/report-column-prefs.ts).' }),
  columnsUnseenStock: def({ key: 'jflo.reports.unseenStock.hiddenColumns_{teamId}_{groupBy}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns, product rows or brand/collection rows (reports/report-column-prefs.ts).' }),
  columnsUnitSales: def({ key: 'unit_sales_hidden_columns_{teamId}_{groupBy}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns per grouping (reports/report-column-prefs.ts).' }),
  columnsProducts: def({ key: 'products_list_hidden_columns_{teamId}_{groupBy}', scope: 'team', clearOnSignOut: false, holds: '/products hidden columns per grouping (reports/report-column-prefs.ts).' }),
  columnsMedia: def({ key: 'media_list_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/media hidden columns (reports/report-column-prefs.ts).' }),
  columnsPlanning: def({ key: 'planning_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Planning hidden columns (reports/report-column-prefs.ts).' }),
  columnsPlanningOptional: def({ key: 'planning_hidden_columns_optional_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Optional Planning columns shown (pages/planning/planning-column-prefs.ts).' }),
  columnsGoogleAds: def({ key: 'google_ads_hidden_columns_{teamId}_{table}', scope: 'team', clearOnSignOut: false, holds: 'Google Ads hidden columns per table (reports/report-column-prefs.ts).' }),
  columnsMarketingPlatform: def({ key: 'jflo.marketing.{platform}.{table}.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Microsoft and Meta Ads hidden columns per table (reports/report-column-prefs.ts).' }),
  columnsSupplierCatalogues: def({ key: 'supplier_catalogues_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (pages/suppliers/catalogues-column-prefs.ts).' }),
  columnsProductBatchGrid: def({ key: 'product_batch_grid_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (pages/product-batch-details/useBatchGridView.ts).' }),
  columnsProductsBulkEdit: def({ key: 'products_bulk_edit_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (pages/products-bulk-edit/column-prefs.ts).' }),
  columnsCollectionsBulkEdit: def({ key: 'collections_bulk_edit_hidden_columns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Hidden columns (pages/collections-bulk-edit/column-prefs.ts).' }),
  columnsVendorsList: def({ key: 'jflo.vendors.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/vendors hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsSuppliersList: def({ key: 'jflo.suppliers.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/suppliers hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsPurchaseOrdersList: def({ key: 'jflo.purchaseOrders.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/planning/purchase-orders hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsProductTypesList: def({ key: 'jflo.productTypes.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/product-types hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsLandingPagesList: def({ key: 'jflo.landingPages.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/landing-pages hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsSwatchesList: def({ key: 'jflo.swatches.list.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/swatches hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsSeoNearMisses: def({ key: 'jflo.marketing.seoNearMisses.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/marketing/seo/near-misses hidden columns (SEO_NEAR_MISSES_COLUMN_PREFS, reports/report-column-prefs.ts).' }),
  columnsTimeSpentEmail: def({ key: 'jflo.reports.timeSpentEmail.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/reports/time-spent Email card hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsTimeSpentRecords: def({ key: 'jflo.reports.timeSpentRecords.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/reports/time-spent per-record table hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsAiUsageAcceptance: def({ key: 'jflo.aiUsage.acceptance.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/ai-usage "Suggestions kept" table hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsPlanningOrdersList: def({ key: 'jflo.planning.orders.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'In-progress planning orders hidden columns on /planning (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsLines: def({ key: 'jflo.imports.lines.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Price list Lines tab hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsReview: def({ key: 'jflo.imports.review.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Price list Review tab hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsCosting: def({ key: 'jflo.imports.costing.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Price list Costing tab hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsPriceChanges: def({ key: 'jflo.imports.priceChanges.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/imports/price-changes hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsPriceLists: def({ key: 'jflo.imports.priceLists.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/imports/price-lists hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsShipments: def({ key: 'jflo.imports.shipments.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/imports/shipments hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsImportsShipmentPlan: def({ key: 'jflo.imports.shipmentPlan.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: "A shipment's Plan table hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts)." }),
  columnsImportsHistory: def({ key: 'jflo.imports.history.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: "An import supplier's landed cost history hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts)." }),
  columnsWholesalePriceLists: def({ key: 'jflo.wholesale.priceLists.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/wholesale/price-lists hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsWholesalePricing: def({ key: 'jflo.wholesale.pricing.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: '/wholesale/pricing hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),
  columnsSearchTermCategories: def({ key: 'jflo.marketing.termCategories.hiddenColumns_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Google Ads › Term categories hidden columns (DataTable columnPrefsKey, reports/report-column-prefs.ts).' }),

  // ── Reports ───────────────────────────────────────────────────────────────────────────────────
  unseenStockCharts: def({ key: 'jflo.reports.unseenStock.charts_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Unseen Stock charts shown (reports/unseen-stock.tsx).' }),
  skuStockCharts: def({ key: 'sku_stock_performance_chart_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'SKU Stock charts shown (reports/sku-stock-chart-prefs.ts).' }),
  skuStockPricePointsView: def({ key: 'sku_stock_price_history_price_points_view', scope: 'browser', clearOnSignOut: false, holds: 'Price-point view (reports/sku-stock-price-point-table.tsx).' }),
  reportPageSize: typed({
    key: 'jflo.reports.{report}.pageSize_{teamId}',
    scope: 'team',
    clearOnSignOut: false,
    holds: "Rows per page on a report's table, a size or 'all' (reports/use-report-page-size.ts).",
    defaultValue: 100 as number | 'all',
    parse: (raw: string) =>
      raw === 'all' ? 'all' : (LIST_PAGE_SIZE_OPTIONS as readonly number[]).includes(Number(raw)) ? Number(raw) : undefined,
    serialize: (value: number | 'all') => String(value),
  }),
  skuStockPriceLookback: def({ key: 'sku_stock_price_history_lookback_days', scope: 'browser', clearOnSignOut: false, holds: 'Price history lookback days (reports/sku-stock-price-history-modal.tsx).' }),

  // ── Marketing ─────────────────────────────────────────────────────────────────────────────────
  // Retired by T-229: the marketing range is in the URL and the shared last view (MARKETING_VIEW_ID);
  // lib/retired-list-views.ts moves it there at load. Still registered so sign-out clears it.
  marketingFilters: def({ key: 'jflo-marketing-filters-v3', scope: 'browser', version: 3, clearOnSignOut: true, holds: 'Retired: marketing period, dates and granularity before T-229 (pages/marketing/marketing-filters-url.ts replaced it).' }),
  marketingMatrixCharts: def({ key: 'marketing_matrix_section_charts_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Matrix chart settings (pages/marketing/marketingMatrixChartPrefs.ts).' }),
  marketingMatrixExpand: def({ key: 'marketing_matrix_expand_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Expanded matrix groups (pages/marketing/MarketingPerformanceMatrix.tsx).' }),
  marketingDismissedAlerts: def({ key: 'marketing_dismissed_alerts_{teamId}', scope: 'team', storage: 'session', clearOnSignOut: true, holds: 'Dismissed alert ids (pages/marketing/MarketingAlertsBell.tsx).' }),
  landingStagingTheme: def({ key: 'jflo.landingPages.stagingTheme.{teamId}', scope: 'team', clearOnSignOut: false, holds: "The team's staging theme (its Shopify id), for the landing page editor's View on the staging theme (pages/marketing/landing-pages/editor.tsx)." }),
  landingPreviewWidth: def({ key: 'jflo.landingPages.previewWidth', scope: 'browser', clearOnSignOut: false, holds: 'Width of the preview beside the blocks in the landing page editor (pages/marketing/landing-pages/editor.tsx).' }),
  socialPicksWeights: def({ key: 'jflo:social-picks-weights:{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Social picks scoring weights (pages/marketing/social-picks/index.tsx).' }),
  socialPicksPenalties: def({ key: 'jflo:social-picks-penalties:{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Social picks penalties (pages/marketing/social-picks/index.tsx).' }),
  coloursRecent: typed({ key: 'jflo.colours.recent_{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Colours picked lately, newest first, for the colour picker (components/ColorField.tsx).', ...jsonCodec<string[]>([], (v) => (Array.isArray(v) ? v.filter((c): c is string => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)).slice(0, 8) : undefined), (v) => v.length === 0) }),

  // ── Remembered list and report views (lib/url-state) ─
  urlLastView: def({ key: 'jflo.lastView.{viewId}_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'A page\'s remembered filters, sort and period as a query string (lib/url-state/last-view.ts).' }),

  // ── Tasks ─────────────────────────────────────────────────────────────────────────────────────
  tasksBoardScroll: def({ key: 'jflo.tasks.scroll.{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Board scroll (pages/tasks/board-scroll.ts).' }),
  tasksCollapsedColumns: def({ key: 'jflo.tasks.collapsed.{teamId}', scope: 'team', clearOnSignOut: false, holds: 'Folded Board columns (pages/tasks/board-scroll.ts).' }),
  // Retired by T-229 (lib/retired-list-views.ts moves it to the Board's last view at load).
  tasksLastView: def({ key: 'jflo.tasks.lastView.{teamId}', scope: 'team', clearOnSignOut: true, holds: 'Retired: last Board filters before T-229 (lib/retired-list-views.ts).' }),
  tasksShowChanges: typed({ key: 'jflo.tasks.showChanges', scope: 'browser', clearOnSignOut: false, holds: '"Show changes" in a task timeline (components/tasks/TaskTimeline.tsx).', ...prefCodec.flag(true) }),
  tasksActivityWidth: def({ key: 'jflo.tasks.activityWidth', scope: 'browser', clearOnSignOut: false, holds: 'Width of the activity pane in the Board pop-up (components/tasks/TaskDetailView.tsx).' }),
  tasksAttachScreenshot: def({ key: 'jflo.tasks.attachScreenshot', scope: 'browser', clearOnSignOut: false, holds: '"Attach a screenshot of this page" (components/tasks/page-screenshot.ts).' }),

  // ── Inbox ─────────────────────────────────────────────────────────────────────────────────────
  inboxLegacyDismissed: typed({ key: 'jflo-inbox-legacy-dismissed_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'The count when "Closed or Resolved" was hidden (pages/inbox/LegacyBanner.tsx).', ...prefCodec.text }),
  inboxNeedsReplyHidden: typed({ key: 'jflo-inbox-needs-reply-hidden_{teamId}', scope: 'team', clearOnSignOut: true, holds: 'The count when "may still need a reply" was hidden (pages/inbox/NeedsReplyBanner.tsx).', ...prefCodec.text }),
  inboxDraft: def({ key: 'jflo:inbox-draft:{draftId}', scope: 'team', clearOnSignOut: true, draft: true, holds: 'A reply or forward draft, by conversation (and message) id (pages/inbox/reply-draft.ts).' }),
  inboxSummaryOpen: def({ key: 'jflo:inbox-summary-open', scope: 'browser', clearOnSignOut: false, holds: 'Summary open (pages/inbox/SummaryBar.tsx).' }),
  inboxSummaryCardOpen: def({ key: 'jflo:inbox-summary-card-open', scope: 'browser', clearOnSignOut: false, holds: 'Summary card open (pages/inbox/SummaryBar.tsx).' }),
  inboxListHidden: def({ key: 'jflo:inbox-list-hidden', scope: 'browser', clearOnSignOut: false, holds: 'List hidden (pages/inbox/index.tsx).' }),
  inboxSort: def({ key: 'jflo:inbox-sort-v2', scope: 'browser', version: 2, clearOnSignOut: false, holds: 'Inbox sort (pages/inbox/index.tsx).' }),
  inboxCustomerHidden: def({ key: 'jflo:inbox-customer-hidden', scope: 'browser', clearOnSignOut: false, holds: 'Customer panel hidden (pages/inbox/ConversationView.tsx).' }),
  inboxComposerHeight: def({ key: 'jflo:inbox-composer-height', scope: 'browser', clearOnSignOut: false, holds: 'Composer height (pages/inbox/ConversationView.tsx).' }),
  inboxComposerExtra: def({ key: 'jflo:inbox-composer-extra', scope: 'browser', clearOnSignOut: true, holds: 'Retired; ConversationView.tsx removes it.' }),

  // ── Receiving, widths and open sections (layout) ──────────────────────────────────────────────
  receivingRemainingOnly: def({ key: 'receiving_show_remaining_only', scope: 'browser', clearOnSignOut: false, holds: 'Show remaining only (pages/receiving/receiving-session-utils.ts).' }),
  receivingDocPanelWidth: def({ key: 'jflo.receiving.docPanelWidth', scope: 'browser', clearOnSignOut: false, holds: 'Document panel width (pages/receiving/receiving-doc-side-panel.tsx).' }),
  sectionVariantDetails: def({ key: 'variant-details.section.{section}', scope: 'browser', clearOnSignOut: false, holds: 'Open sections (pages/variant-details/variant-details-section-prefs.ts).' }),
  sectionProductDetails: def({ key: 'product-details.section.{section}', scope: 'browser', clearOnSignOut: false, holds: 'Open sections (pages/variant-details/variant-details-section-prefs.ts).' }),
  sectionSupplierDetails: def({ key: 'supplier-details.section.{section}', scope: 'browser', clearOnSignOut: false, holds: 'Open sections (pages/supplier-details/section-prefs.ts, supplier-details.tsx).' }),
  sectionBrandDetails: def({ key: 'brand-details.section.{section}', scope: 'browser', clearOnSignOut: false, holds: 'Open sections (pages/vendor-details.tsx).' }),
  sectionProductBatchHeader: def({ key: 'product-batch-header', scope: 'browser', clearOnSignOut: false, holds: 'Open section (pages/product-batch-details/BatchSettingsCard.tsx).' }),
  sectionProductBatchMedia: def({ key: 'product-batch-media', scope: 'browser', clearOnSignOut: false, holds: 'Open section (pages/product-batch-details/BatchMediaTable.tsx).' }),
  sectionProductBatchProducts: def({ key: 'product-batch-products', scope: 'browser', clearOnSignOut: false, holds: 'Open section (pages/product-batch-details/index.tsx).' }),

  // ── Wholesale portal (its own sign-in, on its own host) ───────────────────────────────────────
  portalBasket: def({ key: 'jflo-portal-basket', scope: 'team', clearOnSignOut: false, holds: "The portal basket, emptied when another customer signs in (portal/basket-store.ts); jflo's sign-out doesn't touch it." }),

  // ── Step 2.7 (T-199): keys found unregistered. A block of its own so the open PRs adding to the
  // areas above merge cleanly; move each into its area once they're in. ─────────────────────────
  appPageScroll: def({ key: 'jflo.page-scroll', scope: 'browser', storage: 'session', clearOnSignOut: false, holds: "Window scroll per history entry, this tab only (hooks/use-page-scroll.ts)." }),
} as const satisfies Record<string, PrefDef>;

export type UiPrefId = keyof typeof UI_PREFS;

// ── Keys ─────────────────────────────────────────────────────────────────────────────────────────

const PLACEHOLDER = /\{([^}]+)\}/g;

/**
 * The stored key for these values, or null while one is missing (no team yet): nothing is read
 * or written then.
 */
export function prefKey<K extends string>(pref: PrefDef<K>, ...args: PrefKeyArgs<K>): string | null {
  const params = (args[0] ?? {}) as Record<string, string | null | undefined>;
  let missing = false;
  const key = pref.key.replace(PLACEHOLDER, (_, name: string) => {
    const value = params[name];
    if (!value) missing = true;
    return value ?? '';
  });
  return missing ? null : key;
}

/**
 * The key for a new pref: `jflo.<area>.<name>`, with `.v<N>` from version 2, then `_{teamId}` when
 * it belongs to a team. Old keys keep their names (decision C4).
 */
export function newPrefKey(area: string, name: string, options: { team: boolean; version?: number }): string {
  const version = options.version && options.version > 1 ? `.v${options.version}` : '';
  return `jflo.${area}.${name}${version}${options.team ? '_{teamId}' : ''}`;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const matchers = new Map<string, RegExp>();

/** A key template as a pattern: a team id has no `_`, `.` or `:`; other parts are anything. */
function keyPattern(template: string): RegExp {
  let pattern = matchers.get(template);
  if (!pattern) {
    const parts = template.split(PLACEHOLDER);
    // split with a capture group alternates literal text and placeholder names.
    const source = parts
      .map((part, i) => (i % 2 === 0 ? escapeRegExp(part) : part === 'teamId' ? '[A-Za-z0-9-]+' : '.+'))
      .join('');
    pattern = new RegExp(`^${source}$`);
    matchers.set(template, pattern);
  }
  return pattern;
}

/** The registered prefs a stored key belongs to, in that storage. */
export function findPrefs(key: string, storage: PrefStorage = 'local'): PrefDef[] {
  return Object.values(UI_PREFS as Record<string, PrefDef>).filter(
    (pref) => pref.storage === storage && keyPattern(pref.key).test(key)
  );
}

// ── Reading and writing ──────────────────────────────────────────────────────────────────────────

type KeyStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** The browser's storage, or null where there is none (Node in tests) or it throws (some private modes). */
export function browserStorage(kind: PrefStorage): KeyStore | null {
  try {
    const store = kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** Writes storage refused (full or blocked): they hold for this page until it reloads. */
const unsaved = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();
const slot = (kind: PrefStorage, key: string) => `${kind}:${key}`;

function notify(kind: PrefStorage, key: string): void {
  for (const listener of listeners.get(slot(kind, key)) ?? []) listener();
}

let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs || typeof window === 'undefined' || !window.addEventListener) return;
  watchingOtherTabs = true;
  window.addEventListener('storage', (event) => {
    const kind: PrefStorage | null =
      event.storageArea === browserStorage('local') ? 'local' : event.storageArea === browserStorage('session') ? 'session' : null;
    if (!kind) return;
    if (event.key === null) {
      // Another tab cleared everything.
      for (const id of listeners.keys()) if (id.startsWith(`${kind}:`)) notify(kind, id.slice(kind.length + 1));
      return;
    }
    unsaved.delete(slot(kind, event.key));
    notify(kind, event.key);
  });
}

/** Calls `listener` when the key changes here or in another tab; returns the unsubscribe. */
export function subscribePrefKey(kind: PrefStorage, key: string | null, listener: () => void): () => void {
  if (!key) return () => {};
  watchOtherTabs();
  const id = slot(kind, key);
  let set = listeners.get(id);
  if (!set) listeners.set(id, (set = new Set()));
  set.add(listener);
  return () => {
    set.delete(listener);
    if (!set.size) listeners.delete(id);
  };
}

/** The stored text, or null. Never throws. */
export function readRawPref(kind: PrefStorage, key: string | null): string | null {
  if (!key) return null;
  const id = slot(kind, key);
  if (unsaved.has(id)) return unsaved.get(id) ?? null;
  try {
    return browserStorage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/**
 * Stores the text (null removes the key) and tells this tab's readers. Never throws. False when
 * storage refused it (held for this page only) or there is no key.
 */
export function writeRawPref(kind: PrefStorage, key: string | null, raw: string | null): boolean {
  if (!key) return false;
  const id = slot(kind, key);
  let stored = true;
  try {
    const store = browserStorage(kind);
    if (!store) throw new Error('No storage');
    if (raw === null) store.removeItem(key);
    else store.setItem(key, raw);
    unsaved.delete(id);
  } catch {
    // Full or blocked: keep it for this page, so the choice still shows until a reload.
    unsaved.set(id, raw);
    stored = false;
  }
  notify(kind, key);
  return stored;
}

export function parsePref<T>(pref: Pref<T>, raw: string | null): T {
  if (raw === null) return pref.defaultValue;
  try {
    const value = pref.parse(raw);
    return value === undefined ? pref.defaultValue : value;
  } catch {
    return pref.defaultValue;
  }
}

/** The value for these placeholders; the default while one is missing or nothing is stored. */
export function readPref<T, K extends string>(pref: Pref<T, K>, ...args: PrefKeyArgs<K>): T {
  return parsePref(pref, readRawPref(pref.storage, prefKey(pref, ...args)));
}

/**
 * Stores the value for these placeholders; does nothing while one is missing. False when it
 * wasn't stored (no key yet, or storage refused it and holds it for this page only).
 */
export function writePref<T, K extends string>(pref: Pref<T, K>, value: T, ...args: PrefKeyArgs<K>): boolean {
  return writeRawPref(pref.storage, prefKey(pref, ...args), pref.serialize(value));
}

// ── Sign-out ─────────────────────────────────────────────────────────────────────────────────────

function storedKeys(store: KeyStore): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

/**
 * Whether sign-out would remove a draft (`draft: true`) kept in this browser, from any page, not
 * only the one open: Log out asks first.
 */
export function hasStoredDrafts(
  stores: Partial<Record<PrefStorage, KeyStore | null>> = { local: browserStorage('local'), session: browserStorage('session') }
): boolean {
  // Drafts storage refused (full or blocked) live only in this page, and sign-out drops them too.
  for (const [id, raw] of unsaved) {
    if (raw === null) continue;
    const kind: PrefStorage = id.startsWith('local:') ? 'local' : 'session';
    if (findPrefs(id.slice(kind.length + 1), kind).some((pref) => pref.draft && pref.clearOnSignOut)) return true;
  }
  for (const kind of ['local', 'session'] as const) {
    const store = stores[kind];
    if (!store) continue;
    try {
      if (storedKeys(store).some((key) => findPrefs(key, kind).some((pref) => pref.draft && pref.clearOnSignOut))) return true;
    } catch {
      // Blocked storage holds no drafts.
    }
  }
  return false;
}

/**
 * Removes every pref marked `clearOnSignOut`, for every team, from this browser's localStorage and
 * this tab's sessionStorage (decision 31 a), and scrubs the team data out of kept prefs that hold
 * some. Called by the tab that signs out and by every tab that hears it (each tab has its own
 * sessionStorage). Returns the keys removed or changed.
 */
export function clearPrefsOnSignOut(
  stores: Partial<Record<PrefStorage, KeyStore | null>> = { local: browserStorage('local'), session: browserStorage('session') }
): string[] {
  const removed: string[] = [];
  for (const kind of ['local', 'session'] as const) {
    const store = stores[kind];
    for (const id of [...unsaved.keys()]) {
      const key = id.slice(kind.length + 1);
      if (id.startsWith(`${kind}:`) && findPrefs(key, kind).some((pref) => pref.clearOnSignOut)) {
        unsaved.delete(id);
        notify(kind, key);
      }
    }
    if (!store) continue;
    let keys: string[];
    try {
      keys = storedKeys(store);
    } catch {
      continue;
    }
    for (const key of keys) {
      const matches = findPrefs(key, kind);
      try {
        if (matches.some((pref) => pref.clearOnSignOut)) {
          store.removeItem(key);
          removed.push(key);
          notify(kind, key);
          continue;
        }
        const scrub = matches.find((pref) => pref.scrubOnSignOut)?.scrubOnSignOut;
        const raw = scrub ? store.getItem(key) : null;
        if (!scrub || raw === null) continue;
        let next: string | null;
        try {
          next = scrub(raw);
        } catch {
          next = null; // Unreadable: it can't be trusted to hold no team data.
        }
        if (next === raw) continue;
        if (next === null) store.removeItem(key);
        else store.setItem(key, next);
        removed.push(key);
        notify(kind, key);
      } catch {
        // Blocked storage: nothing more to do.
      }
    }
  }
  return removed;
}

// ── Codecs for registered keys (step 2.7, T-199) ────────────────────────────────────────────────
// The `def` entries above have no value type. A module moving onto readPref/writePref/usePref gives
// its entry one here, beside the code that understands the value, so the registry lines (which open
// PRs edit) stay as they are.

type PrefCodec<T> = Pick<Pref<T>, 'defaultValue' | 'parse' | 'serialize'>;

/** A registered key family with its value type: `readPref(withCodec(UI_PREFS.x, prefCodec.text), …)`. */
export function withCodec<const K extends string, T>(pref: PrefDef<K>, codec: PrefCodec<T>): Pref<T, K> {
  return { ...pref, ...codec };
}

/**
 * JSON. `read` turns the parsed value into one the page can use, or undefined for the default
 * (unreadable JSON is the default too). `removeWhen`: values stored as no key at all.
 */
export function jsonCodec<T>(
  defaultValue: T,
  read: (value: unknown) => T | undefined,
  removeWhen?: (value: T) => boolean
): PrefCodec<T> {
  return {
    defaultValue,
    parse: (raw) => read(JSON.parse(raw)),
    serialize: (value) => (removeWhen?.(value) ? null : JSON.stringify(value)),
  };
}

/** A marker: 'true' while it's set, no key at all otherwise (the sign-in markers). */
export const markerCodec: PrefCodec<boolean> = {
  defaultValue: false,
  parse: (raw) => raw === 'true',
  serialize: (value) => (value ? 'true' : null),
};

/** Removes the key for these placeholders (a `def` entry needs no codec for this). */
export function removePref<K extends string>(pref: PrefDef<K>, ...args: PrefKeyArgs<K>): void {
  writeRawPref(pref.storage, prefKey(pref, ...args), null);
}

/**
 * A zustand `persist` storage on these helpers (the auth and label stores): reads and writes never
 * throw, and a write storage refuses holds for this page until it reloads. Its key must be
 * registered like any other.
 */
export function prefStateStorage(kind: PrefStorage) {
  return {
    getItem: (key: string) => readRawPref(kind, key),
    setItem: (key: string, value: string) => void writeRawPref(kind, key, value),
    removeItem: (key: string) => void writeRawPref(kind, key, null),
  };
}

/** Tests only: forget writes held in memory and every listener. */
export function resetPrefsForTests(): void {
  unsaved.clear();
  listeners.clear();
}
